import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { site } from "@/config/site";

export default function Contact() {
  const [form, setForm] = useState({
    name: "", business: "", email: "", phone: "", city: "", computers: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function onSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setResult(null);
    try {
      const res = await fetch("/api/tickets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          business: form.business.trim() || null,
          email: form.email.trim(),
          phone: form.phone.trim(),
          city: form.city.trim() || null,
          computers: form.computers.trim() || null,
          plan: null,
          message: form.message.trim() || null,
          source: "contact-page",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setResult({ ok: true, ticket: data.ticketNumber });
        setForm({ name: "", business: "", email: "", phone: "", city: "", computers: "", message: "" });
      } else {
        setResult({
          ok: false,
          error: data.detail || "We couldn't submit your message. Please try again shortly.",
        });
      }
    } catch (err) {
      setResult({ ok: false, error: "Network error. Please try again." });
    } finally {
      setSubmitting(false);
    }
  }

  const inputCls =
    "mt-1.5 w-full rounded-md border border-hairline bg-white px-4 py-2.5 text-ink placeholder:text-ink-body/50 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand transition";

  return (
    <>
      <section
        className="text-white"
        style={{ background: "linear-gradient(105deg,#0F2B46 0%,#196CA3 100%)" }}
      >
        <div className="mx-auto max-w-site px-5 lg:px-8 py-16 lg:py-20">
          <div className="rule" style={{ background: "#4FC3F7" }} />
          <div className="eyebrow !text-brand-light">Contact</div>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold leading-tight tracking-tight max-w-3xl">
            Let's protect your data.
          </h1>
          <p className="mt-4 text-lg text-brand-mist max-w-2xl">
            We usually reply the same day.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-site px-5 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* LEFT — quick contact */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="rule" />
              <div className="eyebrow">Call us</div>
              <a
                href={`tel:${site.phoneTel}`}
                className="mt-2 inline-flex items-center gap-3 text-2xl font-bold text-ink hover:text-brand transition-colors"
                data-testid="contact-phone"
              >
                <Phone className="h-5 w-5 text-brand" strokeWidth={1.75} />
                {site.phoneDisplay}
              </a>
              <p className="mt-1 text-sm text-ink-body">{site.businessHours}</p>
            </div>

            <div>
              <div className="rule" />
              <div className="eyebrow">Email</div>
              <a
                href={`mailto:${site.email}`}
                className="mt-2 inline-flex items-center gap-3 text-ink-body hover:text-brand"
                data-testid="contact-email"
              >
                <Mail className="h-4 w-4 text-brand" strokeWidth={1.75} />
                {site.email}
              </a>
            </div>

            <div>
              <div className="rule" />
              <div className="eyebrow">Address</div>
              <div className="mt-2 flex items-start gap-3">
                <MapPin className="h-4 w-4 text-brand mt-1 shrink-0" strokeWidth={1.75} />
                <address className="not-italic text-ink-body leading-relaxed">
                  {site.address.line1}<br />
                  {site.address.line2}<br />
                  {site.address.country}
                </address>
              </div>
            </div>

            <div>
              <div className="rule" />
              <div className="eyebrow">Business hours</div>
              <div className="mt-2 flex items-center gap-3 text-ink-body">
                <Clock className="h-4 w-4 text-brand" strokeWidth={1.75} />
                {site.businessHours}
              </div>
            </div>
          </div>

          {/* RIGHT — form */}
          <div className="lg:col-span-3">
            <div className="rounded-card bg-brand-card p-6 md:p-8">
              <div className="rule" />
              <div className="eyebrow">Send us a message</div>
              <h2 className="mt-2 text-2xl font-bold text-ink">Tell us about your setup.</h2>
              <p className="mt-1 text-sm text-ink-body">
                We'll come back to you within one business day.
              </p>

              {result?.ok ? (
                <div
                  className="mt-6 rounded-card p-5 flex items-start gap-3"
                  style={{ background: "#E8F4EA", borderLeft: "4px solid #2C994D" }}
                  data-testid="form-status-success"
                >
                  <CheckCircle2 className="h-6 w-6 text-leaf-dark mt-0.5 shrink-0" strokeWidth={1.75} />
                  <div>
                    <p className="font-bold text-leaf-dark">Message received — thank you!</p>
                    <p className="mt-1 text-sm text-ink-body">
                      Your enquiry has been logged as ticket{" "}
                      <span className="font-mono font-semibold text-ink">#{result.ticket}</span>.
                      Our team will get back to you within one business day.
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-form" noValidate>
                  <div>
                    <label className="text-sm font-medium text-ink">Your name *</label>
                    <input required value={form.name} onChange={upd("name")} className={inputCls} placeholder="Ravi Sharma" data-testid="form-name" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-ink">Business name</label>
                    <input value={form.business} onChange={upd("business")} className={inputCls} placeholder="Sharma & Co CA" data-testid="form-business" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-ink">Email *</label>
                    <input required type="email" value={form.email} onChange={upd("email")} className={inputCls} placeholder="you@example.com" data-testid="form-email" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-ink">Phone *</label>
                    <input required type="tel" value={form.phone} onChange={upd("phone")} className={inputCls} placeholder="+91 98XXX XXXXX" data-testid="form-phone" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-ink">City</label>
                    <input value={form.city} onChange={upd("city")} className={inputCls} placeholder="Mumbai" data-testid="form-city" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-ink">How many computers?</label>
                    <input value={form.computers} onChange={upd("computers")} className={inputCls} placeholder="e.g. 3" data-testid="form-computers" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-ink">Message</label>
                    <textarea rows={4} value={form.message} onChange={upd("message")} className={`${inputCls} resize-none`} placeholder="Tell us what you'd like to protect and any questions you have." data-testid="form-message" />
                  </div>

                  {result && !result.ok && (
                    <div className="md:col-span-2 rounded-card p-4 flex items-start gap-3" style={{ background: "#FEF2F2", borderLeft: "4px solid #DC2626" }}>
                      <AlertCircle className="h-5 w-5 text-danger mt-0.5 shrink-0" strokeWidth={1.75} />
                      <div className="text-sm text-ink-body">
                        <strong className="text-danger">Couldn't send.</strong>
                        <div className="mt-1">{result.error}</div>
                      </div>
                    </div>
                  )}

                  <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex items-center gap-2 rounded-md text-white px-6 py-3 text-sm font-semibold shadow-card-soft hover:shadow-card-lift hover:-translate-y-px transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{ background: "linear-gradient(100deg,#1D6C35 0%,#2C994D 100%)" }}
                      data-testid="form-submit"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Sending…
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" strokeWidth={1.75} />
                          Send message
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
